import { h, FunctionalComponent } from 'preact';
import { useContext, useMemo, useRef, useEffect } from 'preact/hooks';
import { memo } from 'preact/compat';
import { EnabledFeatureIcon, LoadingFeatureIcon } from '@uw/icons';
import { includes } from 'helpers';
import { ApplicationContext } from 'contexts/use-application-setup';
import { useDynamicSize } from '../../oversized-widget/use-dynamic-size';
import { AccessibilityFeaturesContext } from '../use-accessibility-fetures';
import { AllyFeaturesActions, AllyFeaturesContext } from '../use-ally-features';

const INACTIVE = 0;

interface FeatureStatusProps {
  featureName: string;
}

const FeatureStatusComponent: FunctionalComponent<FeatureStatusProps> = ({ featureName }) => {
  const size = useDynamicSize();
  const { allyFeaturesState } = useContext(AccessibilityFeaturesContext);
  const { allyFeatureDispatch } = useContext(AllyFeaturesContext);
  const { areResourcesLoaded } = useContext(ApplicationContext);

  const {
    isLoading: isLoadingFromStore = false,
    actionState = 0,
    disabledSteps = [],
    actionStatesMeta,
  } = allyFeaturesState[featureName] || {};

  const isLoading = !areResourcesLoaded || isLoadingFromStore;

  const currentActionState = useRef(actionState);

  useEffect(() => {
    if (currentActionState.current === actionState && isLoading) {
      allyFeatureDispatch(AllyFeaturesActions.setLoading({ name: featureName, isLoading: false }));
    } else if (!isLoading) {
      currentActionState.current = actionState;
    }
  }, [actionState, isLoading]);

  const currentStep = useMemo(() => {
    if (!actionState) return 0;

    if (!disabledSteps.length) return actionState;

    // we should found available step in case of disabled ones.
    const enabledStepKeys = Object.keys(actionStatesMeta || {})
      .map((key) => parseInt(key))
      .filter((stepKey) => !includes(disabledSteps, stepKey));

    const step = enabledStepKeys.indexOf(actionState);

    return step;
  }, [actionState, disabledSteps, actionStatesMeta]);

  const shouldShow = isLoading || currentStep !== INACTIVE;
  if (!shouldShow) {
    return null;
  }

  return (
    <span
      className={`uwaw-features__item__enabled ${isLoading ? 'uwaw-features__item__loading' : ''}`}
    >
      {isLoading ? (
        <LoadingFeatureIcon width={size(9, 11)} height={size(6, 8)} />
      ) : (
        <EnabledFeatureIcon width={size(9, 11)} height={size(6, 8)} />
      )}
    </span>
  );
};

export const FeatureStatus = memo(FeatureStatusComponent);
